import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { MailX, MailCheck } from "lucide-react";

export const useLearnerEmails = () => {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => { api.get("/settings/learner-emails").then(({ data }) => setEnabled(data.enabled)).catch(() => {}); }, []);
  return [enabled, setEnabled];
};

export const LearnerEmailsSwitch = ({ canEdit }) => {
  const [enabled, setEnabled] = useLearnerEmails();
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put("/settings/learner-emails", { enabled: v }); setEnabled(v); toast.success(v ? "Learner emails switched ON" : "Learner emails switched OFF — nothing will be emailed to learners"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update"); }
    finally { setBusy(false); }
  };
  if (enabled === null) return null;
  return (
    <div className="max-w-3xl space-y-4 rounded-xl border border-border bg-white p-6" data-testid="learner-emails-settings">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="font-heading text-lg font-semibold">Learner emails</h3>
          <p className="mt-1 text-sm text-muted-foreground">One switch for every email sent to learners and students across the whole app.</p>
        </div>
        <Switch checked={enabled} disabled={!canEdit || busy} onCheckedChange={toggle} data-testid="learner-emails-switch" />
      </div>
      <div className={`flex items-start gap-3 rounded-lg p-3 text-sm ${enabled ? "bg-green-50 text-green-900" : "bg-amber-50 text-amber-900"}`} data-testid="learner-emails-status">
        {enabled ? <MailCheck size={18} className="mt-0.5 shrink-0" /> : <MailX size={18} className="mt-0.5 shrink-0" />}
        <div>
          <p className="font-semibold">{enabled ? "ON — learners and students receive emails" : "OFF — nothing is emailed to learners or students"}</p>
          <p className="mt-0.5 text-xs">Covers Retrofit learner invites, reminders, enrolment and announcement emails, plus CRM course booking emails, certificate expiry reminders, receipts and invoices sent to students. Emails to staff (completion alerts, notifications) and to employers or other contacts still go out.</p>
          {!enabled && <p className="mt-1 text-xs">New Retrofit learners can still be invited — use <strong>Copy invite link</strong> on the Learners page and send it yourself.</p>}
        </div>
      </div>
      {!canEdit && <p className="text-xs text-muted-foreground">Only admins can change this.</p>}
    </div>
  );
};

export const LearnerEmailsBanner = () => {
  const [enabled] = useLearnerEmails();
  if (enabled !== false) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="learner-emails-off-banner">
      <MailX size={16} /> <span><strong>Learner emails are off.</strong> Invites and reminders aren't emailed — use "Copy invite link" next to each invited learner.</span>
      <Link to="/settings?tab=learner-emails" className="ml-auto text-xs font-semibold underline">Change in Settings</Link>
    </div>
  );
};
